/**
 * Majority Judgment Service
 *
 * Types and comparison wrappers for the frontend. All ranking logic lives
 * in ./majorityJudgment (Balinski–Laraki, identical to the backend copy).
 * Grades are ordinal, A (best) to F (worst); medians are single letters.
 */

import {
  GradeCounts as CoreGradeCounts,
  compareMajorityJudgment,
  countsFromGrades,
  normalizeGrade,
  totalCount,
} from './majorityJudgment';

/**
 * Grade type. Medians are now always single letters (Balinski–Laraki
 * majority grade). The blend members remain only so legacy stored values
 * still type-check while they are migrated.
 */
export type Grade = 'A' | 'A/B' | 'B' | 'B/C' | 'C' | 'C/D' | 'D' | 'D/F' | 'F';

/**
 * Grade counts for scalable Balinski gauge computation
 */
export interface GradeCounts {
  A: number;
  B: number;
  C: number;
  D: number;
  F: number;
}

/**
 * Simplified Majority Judgment interface (matches backend schema)
 * Backend stores only essential fields for alphabetical median calculation
 */
export interface MajorityJudgment {
  medianGrade: Grade; // Median grade (e.g., "B" or "A/B")
  judgmentsNumber: number; // Number of grades/judgments contributing to this judgment
  promptsNumber: number; // Number of unique user prompts
  gradeCounts: GradeCounts; // Aggregated counts for Balinski gauge tie-breaking
  gradeDistribution?: string[]; // DEPRECATED: Legacy array format (for backward compatibility)
}


/**
 * Get human-readable description for a grade
 */
export function getGradeDescription(grade: Grade): string {
  const descriptions: Record<Grade, string> = {
    'A': 'super intelligent',
    'A/B': 'almost super intelligent',
    'B': 'generally intelligent',
    'B/C': 'almost generally intelligent',
    'C': 'artificially intelligent',
    'C/D': 'almost artificially intelligent',
    'D': 'unintelligent',
    'D/F': 'almost unintelligent',
    'F': 'failure',
  };
  return descriptions[grade] || 'unknown';
}


/**
 * Compare two majority judgments using Balinski's gauge approach
 * 
 * NOTE: This function now requires K3 rankings to be passed in.
 * Load rankings once at app start using getGradeRankings() from gradeDescriptionsService
 * 
 * Returns:
 *  - Negative if mjA ranks higher than mjB
 *  - Positive if mjB ranks higher than mjA
 *  - 0 if they are tied
 */
export function compareMajorityJudgments(
  mjA: MajorityJudgment,
  mjB: MajorityJudgment,
  _gradeRankings?: Record<string, number>
): number {
  // Stacking is decided from the grade distributions alone, by the
  // Balinski–Laraki majority ranking in ./majorityJudgment. The stored
  // medianGrade is display data; when counts are present it is not
  // consulted, so a stale or legacy blended median cannot affect order.
  // The K3 rankings parameter is accepted for compatibility and ignored:
  // A > B > C > D > F is intrinsic to the scale.
  const mj = compareMajorityJudgment(countsForComparison(mjA), countsForComparison(mjB));
  if (mj !== 0) return mj;

  // Genuine tie under majority judgment. More judgments is weak evidence of
  // more support; this sits outside the method and never overrides it.
  const judgmentsA = mjA.judgmentsNumber ?? 0;
  const judgmentsB = mjB.judgmentsNumber ?? 0;
  return judgmentsB - judgmentsA;
}

/**
 * Build the MajorityJudgment for an aggregate document, attaching the
 * authoritative grade counts.
 *
 * Aggregate documents can carry two `gradeCounts` fields: one nested under
 * `majorityJudgment` (written by the full re-aggregation path) and one at
 * the top level (written by the incremental batch path, which merges and so
 * leaves any older nested copy behind, or never writes one at all — true of
 * ~98% of documents). Reading the nested object alone therefore yields no
 * counts for most documents, and without counts the majority ranking cannot
 * run. The authoritative field is whichever sums to `judgmentsNumber`;
 * failing that, the top-level one, which the incremental path keeps current.
 */
export function readMajorityJudgment(docData: any): MajorityJudgment | null {
  const mj = docData?.majorityJudgment;
  if (!mj || typeof mj !== 'object') return null;
  const total = (c: any) => (c && typeof c === 'object' ? totalCount(c) : -1);
  const J = mj.judgmentsNumber;
  const top = docData.gradeCounts;
  const nested = mj.gradeCounts;
  let counts: GradeCounts | undefined;
  if (top && J != null && total(top) === J) counts = top;
  else if (nested && J != null && total(nested) === J) counts = nested;
  else if (top && total(top) > 0) counts = top;
  else if (nested && total(nested) > 0) counts = nested;
  return counts ? {...mj, gradeCounts: counts} : {...mj};
}

/** Counts to rank by: stored counts, else the median treated as one grade. */
function countsForComparison(mj: MajorityJudgment): CoreGradeCounts {
  if (mj.gradeCounts && totalCount(mj.gradeCounts) > 0) return mj.gradeCounts;
  const c = countsFromGrades([]);
  const l = normalizeGrade(mj.medianGrade);
  if (l) c[l] = 1;
  return c;
}


/**
 * Check if two confidence intervals overlap significantly
 * Used for clustering models with statistically indistinguishable performance
 */
export function confidenceIntervalsOverlap(
  ciA: [number, number],
  ciB: [number, number]
): boolean {
  const [lowerA, upperA] = ciA;
  const [lowerB, upperB] = ciB;
  
  // Check if there's any overlap
  return !(upperA < lowerB || upperB < lowerA);
}
